// Demonstration SIL property inventory for the preview environment.
//
// ⚠️ CRITICAL PRODUCTION SAFEGUARD
// Every record here MUST carry recordVerificationStatus: "demonstration".
// `filterByVerification()` in propertyModel.js hides these records automatically
// when the site is served from the canonical production hostname.
// Do NOT change any record here to "verified" without genuine property + partner data.
//
// Purpose: demonstrate the national accommodation search architecture, filter
// coverage, empty states and location hubs across all 8 Australian states.
//
// Imagery direction:
//   Prefer realistic Australian single-storey suburban homes (brick / render),
//   level-entry, wide frontage. Avoid luxury 2-storey mansions.
//   ~80% single-storey. ~20% accessible townhouses/villas.

// Deterministic SVG placeholder illustrations — see propertyPlaceholders.js
// for rationale. Every demo listing renders a stylised single-storey Australian
// suburban home, deliberately illustrative (not photographic) so viewers
// never mistake it for a real Alpha property photograph.
import { svgHomeDataUrl } from "./propertyPlaceholders";

function homeImage(slug, altSuffix = "") {
  const alt = `Single-storey Australian suburban SIL home in ${altSuffix}`.trim();
  return { src: svgHomeDataUrl(slug, alt), alt };
}

function demo(record) {
  const heroAlt = `Property photo coming soon — ${record.suburb}, ${record.state}`;
  const locationLabel = `${record.suburb}, ${record.state}`;
  const placeholder = { src: svgHomeDataUrl(record.slug, locationLabel), alt: heroAlt };
  // Strip any legacy IMG.* references from the caller so the per-slug
  // placeholder wins deterministically for every demo record.
  // eslint-disable-next-line no-unused-vars
  const { hero_image, images, ...rest } = record;
  return {
    published: true,
    recordVerificationStatus: "demonstration",
    country: "AU",
    hero_image: placeholder.src,
    images: [placeholder],
    ...rest,
  };
}

// Legacy IMG map kept for records that still reference IMG.* — deprecated,
// prefer svgHomeDataUrl(slug) for new records.
const IMG = new Proxy({}, { get: () => svgHomeDataUrl("legacy-placeholder", "Single-storey Australian suburban home") });

export const DEMO_PROPERTIES = [
  // ============ VICTORIA (4) ============
  demo({
    id: "demo-vic-broadmeadows",
    slug: "broadmeadows-sil-home",
    href: "/homes/broadmeadows-sil-home",
    name: "Broadmeadows Level-Entry Home",
    short: "SIL Home · Broadmeadows, VIC",
    suburb: "Broadmeadows",
    city: "melbourne",
    state: "VIC",
    postcode: "3047",
    region: "Melbourne North",
    lat: -37.6832, lon: 144.9195,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 2,
    total_bedrooms: 4,
    participant_bedrooms: 3,
    occupied_bedrooms: 1,
    available_from: "2026-03-01",
    vacancy_last_updated: "2026-02-14",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover", "shared"],
    sda_category: null,
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "accessible-kitchen", "single-storey", "secure-outdoor"],
    suitability_tags: ["intellectual-disability", "psychosocial-disability", "community-transition"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 2,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Broadmeadows train station", shops: "Broadmeadows Central", medical: "Northern Hospital nearby", community: "Local parks and community centre" },
    oneLiner: "A single-storey Broadmeadows SIL home with 3 participant bedrooms, shared 1:1 / 1:2 support and step-free access throughout.",
    hero_image: IMG.suburbanBrick1,
    images: [
      { src: IMG.suburbanBrick1, alt: "Single-storey brick SIL home with level front entry in Broadmeadows, Victoria" },
      { src: IMG.accessibleKitchen, alt: "Open-plan kitchen and living area with step-free floor transitions" },
      { src: IMG.accessibleBathroom, alt: "Accessible bathroom with grab-rail-ready walls and roll-in shower" },
    ],
    features: ["Level driveway", "Wide side pathways", "Open-plan living", "Rear covered patio"],
  }),

  demo({
    id: "demo-vic-werribee",
    slug: "werribee-sil-home",
    href: "/homes/werribee-sil-home",
    name: "Werribee Accessible Villa",
    short: "SIL Villa · Werribee, VIC",
    suburb: "Werribee",
    city: "melbourne",
    state: "VIC",
    postcode: "3030",
    region: "Melbourne West",
    lat: -37.9018, lon: 144.6636,
    listing_status: "limited",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-02-20",
    vacancy_last_updated: "2026-02-15",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "active-overnight", "individualised"],
    sda_category: "improved-liveability",
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "single-storey", "sensory-friendly"],
    suitability_tags: ["physical-disability", "psychosocial-disability", "high-intensity"],
    property_type: "villa",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 1,
    nearby: { transport: "Werribee train station", shops: "Pacific Werribee", medical: "Werribee Mercy Hospital", community: "Werribee Park" },
    oneLiner: "Single-storey accessible villa in Werribee — Improved Liveability SDA, 1:1 or 1:2 support, sensory-friendly interior.",
    hero_image: IMG.suburbanRender1,
    images: [
      { src: IMG.suburbanRender1, alt: "Modern render single-storey accessible villa in Werribee, Victoria" },
      { src: IMG.openPlanLiving, alt: "Open-plan living area with wide circulation space and level flooring" },
    ],
    features: ["Level entry", "Wide corridors", "Sensory-considered finishes", "Secure rear yard"],
  }),

  demo({
    id: "demo-vic-dandenong",
    slug: "dandenong-sil-home",
    href: "/homes/dandenong-sil-home",
    name: "Dandenong Community Home",
    short: "SIL Home · Dandenong, VIC",
    suburb: "Dandenong",
    city: "melbourne",
    state: "VIC",
    postcode: "3175",
    region: "Melbourne South-East",
    lat: -37.9877, lon: 145.2098,
    listing_status: "upcoming",
    vacancy_available: true,
    vacancies: 3,
    total_bedrooms: 4,
    participant_bedrooms: 3,
    occupied_bedrooms: 0,
    available_from: "2026-05-01",
    vacancy_last_updated: "2026-02-10",
    support_ratios: ["2:1", "1:1"],
    support_model: ["24-7", "active-overnight"],
    sda_category: "high-physical-support",
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "accessible-kitchen", "ceiling-hoist", "wheelchair-accessible", "single-storey", "accessible-parking"],
    suitability_tags: ["physical-disability", "complex-health", "high-intensity", "hospital-discharge"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 2,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Dandenong station · bus routes", shops: "Dandenong Plaza", medical: "Dandenong Hospital 5min", community: "Dandenong Park" },
    oneLiner: "Purpose-built High Physical Support SDA in Dandenong — ceiling hoist, 2:1 complex support, opening April 2026.",
    hero_image: IMG.suburbanContemporary1,
    images: [
      { src: IMG.suburbanContemporary1, alt: "Purpose-built single-storey accessible SIL/SDA home with wide frontage in Dandenong, Victoria" },
      { src: IMG.wideHallway, alt: "Wide accessible hallway with level transitions" },
    ],
    features: ["Ceiling hoist provision", "Wide accessible parking", "Level entry", "Zero-threshold bathroom"],
  }),

  demo({
    id: "demo-vic-geelong",
    slug: "geelong-sil-home",
    href: "/homes/geelong-sil-home",
    name: "Geelong North Family Home",
    short: "SIL Home · Geelong North, VIC",
    suburb: "Geelong North",
    city: "melbourne",
    state: "VIC",
    postcode: "3215",
    region: "Geelong",
    lat: -38.1088, lon: 144.3606,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-02-28",
    vacancy_last_updated: "2026-02-15",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover"],
    sda_category: null,
    accessibility_features: ["step-free-entry", "wide-doorways", "single-storey", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "intellectual-disability", "community-transition"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Geelong North bus network", shops: "Corio Village nearby", medical: "University Hospital Geelong", community: "Local parks" },
    oneLiner: "Single-storey Geelong North SIL home — quiet street, 1:1 or 1:2 support, calm sensory-considered interior.",
    hero_image: IMG.suburbanFamily1,
    images: [
      { src: IMG.suburbanFamily1, alt: "Single-storey suburban brick home with wide driveway in Geelong North, Victoria" },
    ],
    features: ["Level entry", "Wide backyard", "Quiet cul-de-sac location"],
  }),

  // ============ NEW SOUTH WALES (3) ============
  demo({
    id: "demo-nsw-blacktown",
    slug: "blacktown-sil-home",
    href: "/homes/blacktown-sil-home",
    name: "Blacktown Accessible Home",
    short: "SIL Home · Blacktown, NSW",
    suburb: "Blacktown",
    city: "sydney",
    state: "NSW",
    postcode: "2148",
    region: "Western Sydney",
    lat: -33.7716, lon: 150.9060,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 2,
    total_bedrooms: 4,
    participant_bedrooms: 3,
    occupied_bedrooms: 1,
    available_from: "2026-03-15",
    vacancy_last_updated: "2026-02-15",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover", "shared"],
    sda_category: "fully-accessible",
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "accessible-kitchen", "wheelchair-accessible", "single-storey", "accessible-parking", "secure-outdoor"],
    suitability_tags: ["physical-disability", "intellectual-disability", "community-transition"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 2,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Blacktown train station", shops: "Westpoint Blacktown", medical: "Blacktown Hospital", community: "Local parks and community facilities" },
    oneLiner: "Fully Accessible SDA in Blacktown — level entry, wheelchair-accessible throughout, shared 1:1 / 1:2 support.",
    hero_image: IMG.suburbanModern1,
    images: [
      { src: IMG.suburbanModern1, alt: "Fully Accessible single-storey SDA home with level entry in Blacktown, NSW" },
    ],
    features: ["Wheelchair accessible", "Wide accessible parking", "Level entry", "Accessible kitchen"],
  }),

  demo({
    id: "demo-nsw-parramatta",
    slug: "parramatta-sil-home",
    href: "/homes/parramatta-sil-home",
    name: "Parramatta Accessible Townhouse",
    short: "SIL Townhouse · Parramatta, NSW",
    suburb: "Parramatta",
    city: "sydney",
    state: "NSW",
    postcode: "2150",
    region: "Greater Sydney",
    lat: -33.8151, lon: 151.0000,
    listing_status: "eoi",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-04-01",
    vacancy_last_updated: "2026-02-12",
    support_ratios: ["1:1"],
    support_model: ["24-7", "active-overnight", "individualised"],
    sda_category: "improved-liveability",
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "lift-access", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "high-intensity"],
    property_type: "townhouse",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 1,
    nearby: { transport: "Parramatta station · light rail", shops: "Westfield Parramatta", medical: "Westmead Hospital nearby", community: "Parramatta Park" },
    oneLiner: "Ground-floor accessible townhouse in Parramatta — Improved Liveability SDA with lift access to upper level.",
    hero_image: IMG.suburbanRender1,
    images: [
      { src: IMG.suburbanRender1, alt: "Accessible ground-floor townhouse with lift access in Parramatta, NSW" },
    ],
    features: ["Ground-floor bedroom", "Lift access", "Level courtyard", "Accessible bathroom"],
  }),

  demo({
    id: "demo-nsw-newcastle",
    slug: "newcastle-sil-home",
    href: "/homes/newcastle-sil-home",
    name: "Newcastle Level-Entry Home",
    short: "SIL Home · Newcastle, NSW",
    suburb: "Charlestown",
    city: "sydney",
    state: "NSW",
    postcode: "2290",
    region: "Newcastle / Lake Macquarie",
    lat: -32.9695, lon: 151.6947,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-03-01",
    vacancy_last_updated: "2026-02-15",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover"],
    sda_category: null,
    accessibility_features: ["step-free-entry", "wide-doorways", "single-storey", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "intellectual-disability"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Charlestown bus interchange", shops: "Charlestown Square", medical: "John Hunter Hospital", community: "Local parks" },
    oneLiner: "Single-storey Charlestown home — level entry, 1:1 or 1:2 support, close to community services and hospitals.",
    hero_image: IMG.suburbanLevelEntry1,
    images: [
      { src: IMG.suburbanLevelEntry1, alt: "Single-storey level-entry SIL home in Charlestown, Newcastle NSW" },
    ],
    features: ["Level driveway", "Ramp-ready entry", "Quiet street"],
  }),

  // ============ QUEENSLAND (3) ============
  demo({
    id: "demo-qld-logan",
    slug: "logan-sil-home",
    href: "/homes/logan-sil-home",
    name: "Logan Accessible Home",
    short: "SIL Home · Logan, QLD",
    suburb: "Springwood",
    city: "brisbane",
    state: "QLD",
    postcode: "4127",
    region: "Logan",
    lat: -27.6099, lon: 153.1291,
    listing_status: "limited",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 4,
    participant_bedrooms: 3,
    occupied_bedrooms: 2,
    available_from: "2026-03-10",
    vacancy_last_updated: "2026-02-13",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "shared"],
    sda_category: "improved-liveability",
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "single-storey", "secure-outdoor"],
    suitability_tags: ["intellectual-disability", "psychosocial-disability", "community-transition"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 2,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Springwood bus interchange", shops: "Springwood Mall", medical: "Logan Hospital", community: "Underwood Park" },
    oneLiner: "Improved Liveability SDA in Springwood, Logan — single-storey, shared 1:1 / 1:2 support, level entry.",
    hero_image: IMG.suburbanBrick1,
    images: [
      { src: IMG.suburbanBrick1, alt: "Single-storey brick accessible home in Springwood, Logan QLD" },
    ],
    features: ["Level entry", "Accessible bathroom", "Rear covered patio"],
  }),

  demo({
    id: "demo-qld-ipswich",
    slug: "ipswich-sil-home",
    href: "/homes/ipswich-sil-home",
    name: "Springfield Complex-Support Home",
    short: "SIL Home · Springfield, QLD",
    suburb: "Springfield",
    city: "brisbane",
    state: "QLD",
    postcode: "4300",
    region: "Ipswich",
    lat: -27.6667, lon: 152.9167,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-02-25",
    vacancy_last_updated: "2026-02-15",
    support_ratios: ["2:1", "1:1"],
    support_model: ["24-7", "active-overnight"],
    sda_category: "robust",
    accessibility_features: ["reinforced-construction", "step-free-entry", "wide-doorways", "single-storey", "sensory-friendly", "secure-outdoor"],
    suitability_tags: ["behaviours-of-concern", "forensic", "complex-health", "high-intensity"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Springfield Central station", shops: "Orion Springfield", medical: "Mater Private Springfield", community: "Robelle Domain" },
    oneLiner: "Robust SDA in Springfield — 2:1 complex support, reinforced construction, sensory-considered design for participants with complex needs.",
    hero_image: IMG.suburbanLevelEntry1,
    images: [
      { src: IMG.suburbanLevelEntry1, alt: "Robust category single-storey SDA home with reinforced construction in Springfield, QLD" },
    ],
    features: ["Reinforced construction", "Secure outdoor area", "Sensory-considered finishes"],
  }),

  demo({
    id: "demo-qld-redcliffe",
    slug: "redcliffe-sil-home",
    href: "/homes/redcliffe-sil-home",
    name: "Redcliffe Coastal Home",
    short: "SIL Home · Redcliffe, QLD",
    suburb: "Redcliffe",
    city: "brisbane",
    state: "QLD",
    postcode: "4020",
    region: "Moreton Bay",
    lat: -27.2306, lon: 153.1042,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 2,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 0,
    available_from: "2026-03-01",
    vacancy_last_updated: "2026-02-14",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "individualised"],
    sda_category: null,
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "single-storey", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "physical-disability"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 2,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Redcliffe Peninsula rail", shops: "Redcliffe Bluewater Square", medical: "Redcliffe Hospital", community: "Redcliffe foreshore" },
    oneLiner: "Single-storey coastal SIL home in Redcliffe — level entry, 1:1 or 1:2 individualised support, walking distance to foreshore.",
    hero_image: IMG.suburbanContemporary1,
    images: [
      { src: IMG.suburbanContemporary1, alt: "Contemporary single-storey coastal SIL home in Redcliffe, QLD" },
    ],
    features: ["Level entry", "Close to foreshore", "Accessible bathroom"],
  }),

  // ============ WESTERN AUSTRALIA (2) ============
  demo({
    id: "demo-wa-joondalup",
    slug: "joondalup-sil-home",
    href: "/homes/joondalup-sil-home",
    name: "Joondalup Community Home",
    short: "SIL Home · Joondalup, WA",
    suburb: "Joondalup",
    city: "perth",
    state: "WA",
    postcode: "6027",
    region: "Perth North",
    lat: -31.7448, lon: 115.7661,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-02-28",
    vacancy_last_updated: "2026-02-15",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover"],
    sda_category: null,
    accessibility_features: ["step-free-entry", "wide-doorways", "single-storey", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "intellectual-disability"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Joondalup train station", shops: "Lakeside Joondalup", medical: "Joondalup Health Campus", community: "Neil Hawkins Park" },
    oneLiner: "Single-storey Joondalup SIL home — quiet suburb, 1:1 or 1:2 support, close to Joondalup Health Campus and Lakeside shopping.",
    hero_image: IMG.suburbanRender1,
    images: [
      { src: IMG.suburbanRender1, alt: "Single-storey render suburban SIL home in Joondalup, Perth WA" },
    ],
    features: ["Level driveway", "Wide side pathways", "Quiet street"],
  }),

  demo({
    id: "demo-wa-rockingham",
    slug: "rockingham-sil-home",
    href: "/homes/rockingham-sil-home",
    name: "Rockingham Accessible Home",
    short: "SIL Home · Rockingham, WA",
    suburb: "Rockingham",
    city: "perth",
    state: "WA",
    postcode: "6168",
    region: "Perth South",
    lat: -32.2764, lon: 115.7297,
    listing_status: "upcoming",
    vacancy_available: true,
    vacancies: 2,
    total_bedrooms: 4,
    participant_bedrooms: 3,
    occupied_bedrooms: 0,
    available_from: "2026-05-15",
    vacancy_last_updated: "2026-02-11",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover", "shared"],
    sda_category: "improved-liveability",
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "accessible-kitchen", "single-storey", "accessible-parking", "secure-outdoor"],
    suitability_tags: ["intellectual-disability", "psychosocial-disability", "community-transition"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 2,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Rockingham train station", shops: "Rockingham Centre", medical: "Rockingham General Hospital", community: "Rockingham foreshore" },
    oneLiner: "Improved Liveability SDA in Rockingham opening mid-2026 — single-storey, shared 1:1 / 1:2 support, close to hospital and beach.",
    hero_image: IMG.suburbanModern1,
    images: [
      { src: IMG.suburbanModern1, alt: "Modern single-storey Improved Liveability SDA home in Rockingham, Perth WA" },
    ],
    features: ["Level entry", "Wide corridors", "Accessible kitchen", "Rear covered outdoor area"],
  }),

  // ============ SOUTH AUSTRALIA (1) ============
  demo({
    id: "demo-sa-salisbury",
    slug: "salisbury-sil-home",
    href: "/homes/salisbury-sil-home",
    name: "Salisbury Level-Entry Home",
    short: "SIL Home · Salisbury, SA",
    suburb: "Salisbury",
    city: "adelaide",
    state: "SA",
    postcode: "5108",
    region: "Northern Adelaide",
    lat: -34.7628, lon: 138.6455,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-03-01",
    vacancy_last_updated: "2026-02-15",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover"],
    sda_category: null,
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "single-storey", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "intellectual-disability", "hospital-discharge"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Salisbury train station", shops: "Salisbury Square", medical: "Lyell McEwin Hospital", community: "Salisbury Community Hub" },
    oneLiner: "Single-storey Salisbury SIL home — level entry, 1:1 or 1:2 support, close to Lyell McEwin Hospital.",
    hero_image: IMG.suburbanFamily1,
    images: [
      { src: IMG.suburbanFamily1, alt: "Single-storey suburban SIL home with level entry in Salisbury, Adelaide SA" },
    ],
    features: ["Level driveway", "Wide accessible bathroom", "Quiet cul-de-sac"],
  }),

  // ============ ACT (1) ============
  demo({
    id: "demo-act-canberra",
    slug: "canberra-sil-home",
    href: "/homes/canberra-sil-home",
    name: "Belconnen Accessible Home",
    short: "SIL Home · Belconnen, ACT",
    suburb: "Belconnen",
    city: "canberra",
    state: "ACT",
    postcode: "2617",
    region: "Canberra",
    lat: -35.2384, lon: 149.0664,
    listing_status: "eoi",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-04-01",
    vacancy_last_updated: "2026-02-13",
    support_ratios: ["1:1"],
    support_model: ["24-7", "individualised"],
    sda_category: "improved-liveability",
    accessibility_features: ["step-free-entry", "wide-doorways", "accessible-bathroom", "single-storey", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "high-intensity"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Belconnen bus interchange", shops: "Westfield Belconnen", medical: "Calvary Hospital Bruce", community: "Lake Ginninderra" },
    oneLiner: "Improved Liveability SDA in Belconnen — single-storey, 1:1 individualised support, expressions of interest open.",
    hero_image: IMG.suburbanLevelEntry1,
    images: [
      { src: IMG.suburbanLevelEntry1, alt: "Single-storey Improved Liveability SDA home in Belconnen, Canberra ACT" },
    ],
    features: ["Level entry", "Quiet street", "Close to Lake Ginninderra"],
  }),

  // ============ TASMANIA (1) ============
  demo({
    id: "demo-tas-hobart",
    slug: "hobart-sil-home",
    href: "/homes/hobart-sil-home",
    name: "Glenorchy Community Home",
    short: "SIL Home · Glenorchy, TAS",
    suburb: "Glenorchy",
    city: "hobart",
    state: "TAS",
    postcode: "7010",
    region: "Hobart",
    lat: -42.8339, lon: 147.2723,
    listing_status: "available",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-03-01",
    vacancy_last_updated: "2026-02-14",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover"],
    sda_category: null,
    accessibility_features: ["step-free-entry", "wide-doorways", "single-storey", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "intellectual-disability"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Glenorchy bus network", shops: "Northgate Shopping Centre", medical: "Royal Hobart Hospital", community: "MONA nearby" },
    oneLiner: "Single-storey SIL home in Glenorchy — level entry, 1:1 or 1:2 support, close to Northgate and Royal Hobart Hospital.",
    hero_image: IMG.suburbanBrick1,
    images: [
      { src: IMG.suburbanBrick1, alt: "Single-storey suburban SIL home in Glenorchy, Hobart TAS" },
    ],
    features: ["Level driveway", "Quiet street", "Wide side pathways"],
  }),

  // ============ NORTHERN TERRITORY (1) ============
  demo({
    id: "demo-nt-palmerston",
    slug: "palmerston-sil-home",
    href: "/homes/palmerston-sil-home",
    name: "Palmerston Level-Entry Home",
    short: "SIL Home · Palmerston, NT",
    suburb: "Palmerston",
    city: "darwin",
    state: "NT",
    postcode: "0830",
    region: "Darwin",
    lat: -12.4863, lon: 130.9843,
    listing_status: "eoi",
    vacancy_available: true,
    vacancies: 1,
    total_bedrooms: 3,
    participant_bedrooms: 2,
    occupied_bedrooms: 1,
    available_from: "2026-05-01",
    vacancy_last_updated: "2026-02-10",
    support_ratios: ["1:1", "1:2"],
    support_model: ["24-7", "sleepover"],
    sda_category: null,
    accessibility_features: ["step-free-entry", "wide-doorways", "single-storey", "secure-outdoor"],
    suitability_tags: ["psychosocial-disability", "intellectual-disability", "community-transition"],
    property_type: "house",
    bathrooms: 2,
    living_areas: 1,
    outdoor_area: true,
    secure_yard: true,
    parking: 2,
    nearby: { transport: "Palmerston bus interchange", shops: "Gateway Shopping Centre", medical: "Palmerston Regional Hospital", community: "Palmerston Water Park" },
    oneLiner: "Single-storey Palmerston SIL home — 1:1 or 1:2 support, level entry, expressions of interest open.",
    hero_image: IMG.suburbanRender1,
    images: [
      { src: IMG.suburbanRender1, alt: "Single-storey suburban SIL home in Palmerston, Darwin NT" },
    ],
    features: ["Level entry", "Wide backyard", "Close to hospital"],
  }),
];
